// GoogleAdsScreen.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Axios from "axios";
import EChartsReact from "echarts-for-react";

import {
    Box,
    Button,
    CircularProgress,
    Container,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
} from "@mui/material";

import FiltersReport from "../components/FiltersReport";
import HoverPaper from "../components/shared/HoverPaper";
import InfoIcon from "../components/InfoIcon";
import NoDataRow from "../components/NoDataRow";
import GoogleAdsForecastSimulator from "../components/GoogleAdsForecastSimulator";
import { getHotelName, URLAPI } from "../configuration";
import { selectHotels } from "../features/hotels/hotelsApi";
import {
    DATATYPE_CHECKIN,
    DATATYPE_RESDATE,
    getComparisonDates,
    PRESET_THIS_QUARTER,
    PRESET_YEAR_TO_DATE,
    process_,
} from "../helpers/filters";
import DataLoader from "../components/DataLoader";
import Colours from "../helpers/colours";
import { setHotelID } from "../actions/hotelID";
import ReportBuilder from "../components/shared/ReportBuilder/ReportBuilder";
import { REPORT_TABLE_METRICS_SUMMARY } from "../components/shared/ReportBuilder/ReportTableMetricsSummary";
import SmallPercentage from "../components/SmallPercentage";

const GOOGLE_ADS_HOTEL_COLUMNS = [
    { key: "AccountCount", label: "Accounts", total: true },
    { key: "CampaignCount", label: "Campaigns", total: true },
    { key: "EnabledCampaignCount", label: "Active campaigns", total: true },
    { key: "Impressions", label: "Impressions", total: true },
    { key: "Clicks", label: "Clicks", total: true },
    { key: "Conversions", label: "Conversions", decimals: 2, total: true },
    { key: "CostConverted", label: "Spend", currency: true, total: true },
];
const GOOGLE_ADS_HOTEL_TABLE_COLUMNS = [
    {
        key: "FirstDataDate",
        label: "First data",
        date: true,
        format: (row) =>
            row.FirstDataDate
                ? new Date(row.FirstDataDate).toLocaleDateString("en-GB", {
                      timeZone: "UTC",
                  })
                : "—",
    },
    {
        key: "LastDataDate",
        label: "Last data",
        date: true,
        format: (row) =>
            row.LastDataDate
                ? new Date(row.LastDataDate).toLocaleDateString("en-GB", {
                      timeZone: "UTC",
                  })
                : "—",
    },
    {
        key: "DataCoveragePercent",
        label: "Coverage",
        format: (row) =>
            row.DataCoveragePercent == null
                ? "—"
                : `${Number(row.DataCoveragePercent).toLocaleString("en-GB", { maximumFractionDigits: 1 })}% (${Number(row.MissingDayCount).toLocaleString("en-GB")} missing)`,
    },
    ...GOOGLE_ADS_HOTEL_COLUMNS,
];

const GoogleAdsHotelsTable = ({
    data = [],
    loading = false,
    error = "",
    onSelectHotel,
}) => {
    const dispatch = useDispatch();
    const { id: hotelID = "default" } = useSelector((state) => state.hotelID);
    const { data: hotels = [] } = useSelector(selectHotels);

    const [sortMetric, setSortMetric] = useState("HotelID");
    const [sortDirection, setSortDirection] = useState("asc");

    const rows = useMemo(
        () =>
            [...data].sort((a, b) => {
                const comparison =
                    sortMetric === "HotelID"
                        ? String(
                              getHotelName(a.HotelID, hotels, false) ??
                                  a.HotelID,
                          ).localeCompare(
                              String(
                                  getHotelName(b.HotelID, hotels, false) ??
                                      b.HotelID,
                              ),
                          )
                        : GOOGLE_ADS_HOTEL_TABLE_COLUMNS.some(
                                ({ key, date }) => key === sortMetric && date,
                            )
                          ? (Date.parse(a[sortMetric]) || 0) -
                            (Date.parse(b[sortMetric]) || 0)
                          : (Number(a[sortMetric]) || 0) -
                            (Number(b[sortMetric]) || 0);

                return sortDirection === "asc" ? comparison : -comparison;
            }),
        [data, hotels, sortMetric, sortDirection],
    );

    const totals = useMemo(
        () =>
            Object.fromEntries(
                GOOGLE_ADS_HOTEL_COLUMNS.map((column) => [
                    column.key,
                    data.reduce(
                        (total, row) => total + Number(row[column.key] ?? 0),
                        0,
                    ),
                ]),
            ),
        [data],
    );

    const handleSort = (metric) => {
        setSortDirection(
            sortMetric === metric && sortDirection === "asc" ? "desc" : "asc",
        );
        setSortMetric(metric);
    };

    return (
        <>
            <InfoIcon
                title="Hotels with Google Ads"
                description="Hotels with active Google Ads mappings and their performance for the selected period."
            />

            <Box component={HoverPaper}>
                <TableContainer sx={{ overflow: "auto", maxHeight: 500 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    <TableSortLabel
                                        active={sortMetric === "HotelID"}
                                        direction={
                                            sortMetric === "HotelID"
                                                ? sortDirection
                                                : "asc"
                                        }
                                        onClick={() => handleSort("HotelID")}
                                    >
                                        Hotel
                                    </TableSortLabel>
                                </TableCell>

                                {GOOGLE_ADS_HOTEL_TABLE_COLUMNS.map(
                                    (column) => (
                                        <TableCell
                                            key={column.key}
                                            align="right"
                                            sx={{
                                                fontWeight: "bold",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            <TableSortLabel
                                                active={
                                                    sortMetric === column.key
                                                }
                                                direction={
                                                    sortMetric === column.key
                                                        ? sortDirection
                                                        : "desc"
                                                }
                                                onClick={() =>
                                                    handleSort(column.key)
                                                }
                                            >
                                                {column.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ),
                                )}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={
                                            GOOGLE_ADS_HOTEL_TABLE_COLUMNS.length +
                                            1
                                        }
                                        align="center"
                                    >
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            ) : error ? (
                                <NoDataRow text={error} />
                            ) : rows.length ? (
                                <>
                                    {rows.map((row) => (
                                        <TableRow
                                            key={row.HotelID}
                                            hover
                                            selected={
                                                String(row.HotelID) ===
                                                String(hotelID)
                                            }
                                            onClick={() => {
                                                dispatch(
                                                    setHotelID(row.HotelID),
                                                );
                                                onSelectHotel?.(row.HotelID);
                                            }}
                                            sx={{
                                                cursor: "pointer",
                                                "&.Mui-selected, &.Mui-selected:hover":
                                                    {
                                                        backgroundColor:
                                                            Colours.selectedRow,
                                                    },
                                            }}
                                        >
                                            <TableCell
                                                sx={{ whiteSpace: "nowrap" }}
                                            >
                                                {getHotelName(
                                                    row.HotelID,
                                                    hotels,
                                                    false,
                                                ) ?? `Hotel ${row.HotelID}`}
                                            </TableCell>

                                            {GOOGLE_ADS_HOTEL_TABLE_COLUMNS.map(
                                                (column) => (
                                                    <TableCell
                                                        key={column.key}
                                                        align="right"
                                                        sx={{
                                                            whiteSpace:
                                                                "nowrap",
                                                        }}
                                                    >
                                                        {column.format
                                                            ? column.format(row)
                                                            : Number(
                                                                  row[
                                                                      column.key
                                                                  ] ?? 0,
                                                              ).toLocaleString(
                                                                  "en-GB",
                                                                  column.currency
                                                                      ? {
                                                                            style: "currency",
                                                                            currency:
                                                                                row.ConvertedCurrencyCode ||
                                                                                "EUR",
                                                                        }
                                                                      : {
                                                                            maximumFractionDigits:
                                                                                column.decimals ??
                                                                                0,
                                                                        },
                                                              )}
                                                    </TableCell>
                                                ),
                                            )}
                                        </TableRow>
                                    ))}

                                    <TableRow
                                        sx={{
                                            "& td": {
                                                fontWeight: "bold",
                                                borderTop: 2,
                                            },
                                        }}
                                    >
                                        <TableCell>Total</TableCell>
                                        {GOOGLE_ADS_HOTEL_TABLE_COLUMNS.map(
                                            (column) => (
                                                <TableCell
                                                    key={column.key}
                                                    align="right"
                                                >
                                                    {column.total
                                                        ? Number(
                                                              totals[
                                                                  column.key
                                                              ],
                                                          ).toLocaleString(
                                                              "en-GB",
                                                              column.currency
                                                                  ? {
                                                                        style: "currency",
                                                                        currency:
                                                                            data[0]
                                                                                ?.ConvertedCurrencyCode ||
                                                                            "EUR",
                                                                    }
                                                                  : {
                                                                        maximumFractionDigits:
                                                                            column.decimals ??
                                                                            0,
                                                                    },
                                                          )
                                                        : ""}
                                                </TableCell>
                                            ),
                                        )}
                                    </TableRow>
                                </>
                            ) : (
                                <NoDataRow text="No Google Ads data for this period" />
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </>
    );
};

const GoogleAdsHotelsChart = ({ data = [], loading = false, error = "" }) => {
    const { data: hotels = [] } = useSelector(selectHotels);
    const [chartMetric, setChartMetric] = useState("CostConverted");

    const chartRows = useMemo(
        () =>
            [...data]
                .filter((row) => Number(row[chartMetric]))
                .sort(
                    (a, b) =>
                        (Number(a[chartMetric]) || 0) -
                        (Number(b[chartMetric]) || 0),
                ),
        [data, chartMetric],
    );

    const chartOption = useMemo(() => {
        const column = GOOGLE_ADS_HOTEL_COLUMNS.find(
            ({ key }) => key === chartMetric,
        );

        return {
            tooltip: {
                trigger: "axis",
                axisPointer: { type: "shadow" },
                valueFormatter: (value) =>
                    Number(value).toLocaleString(
                        "en-GB",
                        column.currency
                            ? {
                                  style: "currency",
                                  currency:
                                      data[0]?.ConvertedCurrencyCode || "EUR",
                              }
                            : {
                                  maximumFractionDigits: column.decimals ?? 0,
                              },
                    ),
            },
            grid: {
                left: 20,
                right: 30,
                top: 20,
                bottom: 20,
                containLabel: true,
            },
            xAxis: {
                type: "value",
            },
            yAxis: {
                type: "category",
                data: chartRows.map(
                    ({ HotelID }) =>
                        getHotelName(HotelID, hotels, false) ??
                        `Hotel ${HotelID}`,
                ),
            },
            series: [
                {
                    name: column.label,
                    type: "bar",
                    data: chartRows.map((row) => Number(row[chartMetric]) || 0),
                },
            ],
        };
    }, [chartMetric, chartRows, data, hotels]);

    if (!loading && chartRows.length > 0) {
        return (
            <>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ mt: 3 }}
                >
                    <InfoIcon
                        title="Google Ads hotel comparison"
                        description="Comparison between hotels using the selected Google Ads metric."
                    />

                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Metric</InputLabel>

                        <Select
                            value={chartMetric}
                            label="Metric"
                            onChange={({ target }) =>
                                setChartMetric(target.value)
                            }
                        >
                            {GOOGLE_ADS_HOTEL_COLUMNS.map((column) => (
                                <MenuItem key={column.key} value={column.key}>
                                    {column.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <Box component={HoverPaper}>
                    <EChartsReact
                        option={chartOption}
                        notMerge
                        lazyUpdate
                        style={{
                            height: Math.max(400, chartRows.length * 34),
                        }}
                    />
                </Box>
            </>
        );
    } else {
        return (
            <Box component={HoverPaper} sx={{ mt: 3, p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    {loading
                        ? "Loading chart..."
                        : "No data available for the selected period"}
                </Typography>
            </Box>
        );
    }
};

const GOOGLE_ADS_SUMMARY_METRICS = {
    adsImpressions: {
        short: "Impressions",
        tooltip: "Google Ads impressions during the selected period.",
    },
    adsClicks: {
        short: "Clicks",
        tooltip: "Google Ads clicks during the selected period.",
    },
    adsCtr: {
        short: "CTR",
        tooltip: "Google Ads clicks divided by impressions.",
        format: { right: "%", maxDecimals: 2 },
    },
    adsConversions: {
        short: "Ads conversions",
        tooltip:
            "Conversions reported by Google Ads. Their meaning depends on the configured conversion actions.",
        format: { maxDecimals: 2 },
    },
    adsSpend: {
        short: "Spend",
        tooltip: "Google Ads spend during the selected period.",
        format: { left: "currency", maxDecimals: 2 },
    },
    adsCpc: {
        short: "Cost per click",
        tooltip: "Google Ads spend divided by clicks.",
        format: { left: "currency", maxDecimals: 2 },
    },
    adsCostPerConversion: {
        short: "Cost per conversion",
        tooltip: "Google Ads spend divided by Google Ads conversions.",
        format: { left: "currency", maxDecimals: 2 },
    },
    adsConversionRate: {
        short: "Ads conversion rate",
        tooltip: "Google Ads conversions divided by clicks.",
        format: { right: "%", maxDecimals: 2 },
    },
    hotelBookings: {
        short: "Bookings",
        tooltip:
            "Non-cancelled room bookings made during the selected period, based on reservation date.",
    },
    hotelRevenue: {
        short: "Booking revenue",
        tooltip:
            "Revenue from non-cancelled room bookings made during the selected period.",
        format: { left: "currency", maxDecimals: 2 },
    },
    blendedCostPerBooking: {
        short: "Spend per booking",
        tooltip:
            "Google Ads spend divided by all non-cancelled website bookings. This is not attributed cost per acquisition.",
        format: { left: "currency", maxDecimals: 2 },
    },
    blendedRevenueToSpend: {
        short: "Revenue / spend",
        tooltip:
            "All non-cancelled website booking revenue divided by Google Ads spend. This is not attributed Google Ads ROAS.",
        format: { right: "x", maxDecimals: 2 },
    },
};

const formatGoogleAdsNumber = (value, decimals = 0) =>
    Number(value || 0).toLocaleString("en-GB", {
        maximumFractionDigits: decimals,
    });

const formatGoogleAdsCurrency = (value, currency) =>
    Number(value || 0).toLocaleString("en-GB", {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
    });

const GOOGLE_ADS_CAMPAIGN_COLUMNS = [
    { key: "CostConverted", label: "Cost", currency: true, decimals: 2 },
    { key: "Impressions", label: "Impressions" },
    { key: "Clicks", label: "Clicks" },
    { key: "Ctr", label: "CTR", percent: true, decimals: 2 },
    { key: "Conversions", label: "Ads conversions", decimals: 2 },
    {
        key: "ConversionValue",
        label: "Conversion value",
        currency: true,
        decimals: 2,
    },
];

const GoogleAdsCampaignsTable = ({
    data = [],
    previousData = [],
    loading = false,
    error = "",
}) => {
    const rows = useMemo(() => {
        const previous = new Map(
            previousData.map((row) => [
                `${row.GoogleCustomerID}:${row.CampaignID}`,
                row,
            ]),
        );

        const getMetrics = (row = {}) => {
            const impressions = Number(row.Impressions) || 0;
            const clicks = Number(row.Clicks) || 0;

            return {
                CostConverted: Number(row.CostConverted) || 0,
                Impressions: impressions,
                Clicks: clicks,
                Ctr: impressions ? (clicks / impressions) * 100 : 0,
                Conversions: Number(row.Conversions) || 0,
                ConversionValue: Number(row.ConversionValue) || 0,
            };
        };

        return data
            .map((row) => {
                const previousRow = previous.get(
                    `${row.GoogleCustomerID}:${row.CampaignID}`,
                );

                return {
                    ...row,
                    metrics: getMetrics(row),
                    previousMetrics: previousRow
                        ? getMetrics(previousRow)
                        : null,
                };
            })
            .sort((a, b) => b.metrics.CostConverted - a.metrics.CostConverted);
    }, [data, previousData]);

    return (
        <Box sx={{ mt: 3 }}>
            <InfoIcon
                title="Performance by campaign"
                description="Campaign performance compared with the immediately preceding period of the same length."
            />

            <Box component={HoverPaper}>
                <TableContainer sx={{ overflow: "auto" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    Campaign
                                </TableCell>

                                {GOOGLE_ADS_CAMPAIGN_COLUMNS.map((column) => (
                                    <TableCell
                                        key={column.key}
                                        align="right"
                                        sx={{
                                            fontWeight: "bold",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={
                                            GOOGLE_ADS_CAMPAIGN_COLUMNS.length +
                                            1
                                        }
                                        align="center"
                                    >
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            ) : error ? (
                                <NoDataRow text={error} />
                            ) : rows.length ? (
                                rows.map((row) => (
                                    <TableRow
                                        key={`${row.GoogleCustomerID}:${row.CampaignID}`}
                                        hover
                                    >
                                        <TableCell>
                                            {row.CampaignName || row.CampaignID}
                                        </TableCell>

                                        {GOOGLE_ADS_CAMPAIGN_COLUMNS.map(
                                            (column) => {
                                                const value =
                                                    row.metrics[column.key];
                                                const previousValue =
                                                    row.previousMetrics?.[
                                                        column.key
                                                    ];
                                                const currency =
                                                    row.ConvertedCurrencyCode ||
                                                    row.AccountCurrencyCode ||
                                                    "EUR";

                                                return (
                                                    <TableCell
                                                        key={column.key}
                                                        align="right"
                                                        sx={{
                                                            whiteSpace:
                                                                "nowrap",
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                justifyContent:
                                                                    "flex-end",
                                                                gap: 0.5,
                                                            }}
                                                        >
                                                            <span>
                                                                {column.currency
                                                                    ? formatGoogleAdsCurrency(
                                                                          value,
                                                                          currency,
                                                                      )
                                                                    : `${formatGoogleAdsNumber(
                                                                          value,
                                                                          column.decimals,
                                                                      )}${column.percent ? "%" : ""}`}
                                                            </span>

                                                            <SmallPercentage
                                                                brackets={false}
                                                                tooltip={false}
                                                                bubble={true}
                                                                bubbleRound={
                                                                    true
                                                                }
                                                                actual={value}
                                                                old={
                                                                    previousValue
                                                                }
                                                                actualName="Selected period"
                                                                oldName="Comparison period"
                                                                context={
                                                                    row.CampaignName
                                                                }
                                                                formatter={(
                                                                    metricValue,
                                                                ) =>
                                                                    column.currency
                                                                        ? formatGoogleAdsCurrency(
                                                                              metricValue,
                                                                              currency,
                                                                          )
                                                                        : `${formatGoogleAdsNumber(
                                                                              metricValue,
                                                                              column.decimals,
                                                                          )}${column.percent ? "%" : ""}`
                                                                }
                                                                styles={{
                                                                    fontSize:
                                                                        "0.75rem",
                                                                    flexShrink: 0,
                                                                }}
                                                            />
                                                        </Box>
                                                    </TableCell>
                                                );
                                            },
                                        )}
                                    </TableRow>
                                ))
                            ) : (
                                <NoDataRow text="No campaigns for this period" />
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};

const GoogleAdsConversionActions = ({
    data = [],
    loading = false,
    error = "",
    validHotel = false,
}) => {
    const rows = useMemo(() => {
        const grouped = {};

        data.forEach((row) => {
            const key = `${row.ConversionActionName ?? "Unknown"}|${
                row.ConversionCategory ?? "Unknown"
            }`;

            grouped[key] ??= {
                ConversionActionName:
                    row.ConversionActionName ?? "Unknown action",
                ConversionCategory:
                    row.ConversionCategory ?? "Unknown category",
                Conversions: 0,
                AllConversions: 0,
                ConversionValue: 0,
                AllConversionValue: 0,
            };

            grouped[key].Conversions += Number(row.Conversions) || 0;
            grouped[key].AllConversions += Number(row.AllConversions) || 0;
            grouped[key].ConversionValue += Number(row.ConversionValue) || 0;
            grouped[key].AllConversionValue +=
                Number(row.AllConversionValue) || 0;
        });

        const result = Object.values(grouped).sort(
            (a, b) => b.Conversions - a.Conversions,
        );

        if (result.length) {
            result.push({
                ConversionActionName: "Total",
                ConversionCategory: "",
                Conversions: result.reduce(
                    (total, row) => total + row.Conversions,
                    0,
                ),
                AllConversions: result.reduce(
                    (total, row) => total + row.AllConversions,
                    0,
                ),
                ConversionValue: result.reduce(
                    (total, row) => total + row.ConversionValue,
                    0,
                ),
                AllConversionValue: result.reduce(
                    (total, row) => total + row.AllConversionValue,
                    0,
                ),
                isTotal: true,
            });
        }

        return result;
    }, [data]);

    return (
        <Box sx={{ mt: 3 }}>
            <InfoIcon
                title="Conversion actions"
                description="Google Ads conversions grouped by conversion action and category."
            />

            <Box component={HoverPaper}>
                <TableContainer sx={{ overflow: "auto" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    Conversion action
                                </TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    Category
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: "bold" }}
                                >
                                    Conversions
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: "bold" }}
                                >
                                    All conversions
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: "bold" }}
                                >
                                    Conversion value
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: "bold" }}
                                >
                                    All conversion value
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {!validHotel ? (
                                <NoDataRow text="Select a hotel." />
                            ) : loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            ) : error ? (
                                <NoDataRow text={error} />
                            ) : rows.length ? (
                                rows.map((row) => (
                                    <TableRow
                                        key={
                                            row.isTotal
                                                ? "total"
                                                : `${row.ConversionActionName}-${row.ConversionCategory}`
                                        }
                                        sx={
                                            row.isTotal
                                                ? {
                                                      "& td": {
                                                          fontWeight: "bold",
                                                          borderTop: 2,
                                                      },
                                                  }
                                                : undefined
                                        }
                                    >
                                        <TableCell>
                                            {row.ConversionActionName}
                                        </TableCell>
                                        <TableCell>
                                            {row.ConversionCategory || "—"}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatGoogleAdsNumber(
                                                row.Conversions,
                                                2,
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatGoogleAdsNumber(
                                                row.AllConversions,
                                                2,
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatGoogleAdsNumber(
                                                row.ConversionValue,
                                                2,
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatGoogleAdsNumber(
                                                row.AllConversionValue,
                                                2,
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <NoDataRow text="No conversion actions for this period" />
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};

const GoogleAdsDevicePerformance = ({
    data = [],
    loading = false,
    error = "",
    validHotel = false,
}) => {
    const currency =
        data.find(({ ConvertedCurrencyCode }) => ConvertedCurrencyCode)
            ?.ConvertedCurrencyCode || "EUR";

    const rows = useMemo(() => {
        const grouped = {};

        data.forEach((row) => {
            const device = row.Device || "Unknown";

            grouped[device] ??= {
                Device: device,
                Impressions: 0,
                Clicks: 0,
                Conversions: 0,
                CostConverted: 0,
            };

            grouped[device].Impressions += Number(row.Impressions) || 0;
            grouped[device].Clicks += Number(row.Clicks) || 0;
            grouped[device].Conversions += Number(row.Conversions) || 0;
            grouped[device].CostConverted += Number(row.CostConverted) || 0;
        });

        const result = Object.values(grouped).sort(
            (a, b) => b.CostConverted - a.CostConverted,
        );

        if (result.length) {
            result.push({
                Device: "Total",
                Impressions: result.reduce(
                    (total, row) => total + row.Impressions,
                    0,
                ),
                Clicks: result.reduce((total, row) => total + row.Clicks, 0),
                Conversions: result.reduce(
                    (total, row) => total + row.Conversions,
                    0,
                ),
                CostConverted: result.reduce(
                    (total, row) => total + row.CostConverted,
                    0,
                ),
                isTotal: true,
            });
        }

        return result;
    }, [data]);

    return (
        <Box sx={{ mt: 3 }}>
            <InfoIcon
                title="Performance by device"
                description="Google Ads performance grouped by device for the selected hotel."
            />

            <Box component={HoverPaper}>
                <TableContainer sx={{ overflow: "auto" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    Device
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: "bold" }}
                                >
                                    Impressions
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: "bold" }}
                                >
                                    Clicks
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: "bold" }}
                                >
                                    CTR
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: "bold" }}
                                >
                                    Conversions
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: "bold" }}
                                >
                                    Conversion rate
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: "bold" }}
                                >
                                    Spend
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: "bold" }}
                                >
                                    CPC
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: "bold" }}
                                >
                                    Cost / conversion
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {!validHotel ? (
                                <NoDataRow text="Select a hotel." />
                            ) : loading ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center">
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            ) : error ? (
                                <NoDataRow text={error} />
                            ) : rows.length ? (
                                rows.map((row) => (
                                    <TableRow
                                        key={row.Device}
                                        sx={
                                            row.isTotal
                                                ? {
                                                      "& td": {
                                                          fontWeight: "bold",
                                                          borderTop: 2,
                                                      },
                                                  }
                                                : undefined
                                        }
                                    >
                                        <TableCell>{row.Device}</TableCell>
                                        <TableCell align="right">
                                            {formatGoogleAdsNumber(
                                                row.Impressions,
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatGoogleAdsNumber(row.Clicks)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatGoogleAdsNumber(
                                                row.Impressions
                                                    ? (row.Clicks /
                                                          row.Impressions) *
                                                          100
                                                    : 0,
                                                2,
                                            )}
                                            %
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatGoogleAdsNumber(
                                                row.Conversions,
                                                2,
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatGoogleAdsNumber(
                                                row.Clicks
                                                    ? (row.Conversions /
                                                          row.Clicks) *
                                                          100
                                                    : 0,
                                                2,
                                            )}
                                            %
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatGoogleAdsCurrency(
                                                row.CostConverted,
                                                currency,
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatGoogleAdsCurrency(
                                                row.Clicks
                                                    ? row.CostConverted /
                                                          row.Clicks
                                                    : 0,
                                                currency,
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatGoogleAdsCurrency(
                                                row.Conversions
                                                    ? row.CostConverted /
                                                          row.Conversions
                                                    : 0,
                                                currency,
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <NoDataRow text="No device data for this period" />
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};

const GoogleAdsDailyCostChart = ({
    data = [],
    loading = false,
    error = "",
    selection = { hotelID: null },
    onSelectAll,
}) => {
    const { data: hotels = [] } = useSelector(selectHotels);
    const [granularity, setGranularity] = useState("day");

    const currency =
        data.find(({ ConvertedCurrencyCode }) => ConvertedCurrencyCode)
            ?.ConvertedCurrencyCode || "EUR";

    const chartOption = useMemo(() => {
        const grouped = {};

        data.forEach(({ HotelID, ReportDate, CostConverted }) => {
            let period = String(ReportDate).split("T")[0];

            if (/^\d+$/.test(period))
                period = new Date(Number(period)).toISOString().slice(0, 10);

            if (granularity === "month") {
                period = period.slice(0, 7);
            } else if (granularity === "week") {
                const monday = new Date(`${period}T00:00:00Z`);
                monday.setUTCDate(
                    monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7),
                );
                period = monday.toISOString().slice(0, 10);
            }

            grouped[HotelID] ??= {};
            grouped[HotelID][period] =
                (grouped[HotelID][period] ?? 0) + (Number(CostConverted) || 0);
        });

        const dates = [
            ...new Set(
                Object.values(grouped).flatMap((costs) => Object.keys(costs)),
            ),
        ].sort();

        const selectedHotelExists = Object.keys(grouped).some(
            (HotelID) => String(HotelID) === String(selection.hotelID),
        );

        return {
            tooltip: {
                trigger: "axis",
                valueFormatter: (value) =>
                    formatGoogleAdsCurrency(value, currency),
            },
            legend: {
                type: "scroll",
                top: 5,
                selected: Object.fromEntries(
                    Object.keys(grouped).map((HotelID) => [
                        getHotelName(HotelID, hotels, false) ??
                            `Hotel ${HotelID}`,
                        selection.hotelID == null ||
                            !selectedHotelExists ||
                            String(HotelID) === String(selection.hotelID),
                    ]),
                ),
            },
            grid: {
                left: 20,
                right: 30,
                top: 65,
                bottom: 20,
                containLabel: true,
            },
            xAxis: {
                type: "category",
                boundaryGap: false,
                data: dates,
                axisLabel: { hideOverlap: true },
            },
            yAxis: {
                type: "value",
                axisLabel: {
                    formatter: (value) =>
                        Number(value).toLocaleString("en-GB", {
                            style: "currency",
                            currency,
                            maximumFractionDigits: 0,
                        }),
                },
            },
            series: Object.entries(grouped).map(([HotelID, costs]) => ({
                name:
                    getHotelName(HotelID, hotels, false) ?? `Hotel ${HotelID}`,
                type: "line",
                smooth: true,
                showSymbol: dates.length <= 31,
                data: dates.map((date) => costs[date] ?? null),
                emphasis: { focus: "series" },
            })),
        };
    }, [currency, data, granularity, hotels, selection]);

    return (
        <Box sx={{ mt: 3 }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                spacing={1}
            >
                <InfoIcon
                    title={`${
                        {
                            day: "Daily",
                            week: "Weekly",
                            month: "Monthly",
                        }[granularity]
                    } spend by hotel`}
                    description={`Google Ads spend per ${granularity} for every hotel.`}
                />

                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={!data.length}
                        onClick={onSelectAll}
                    >
                        Select all
                    </Button>

                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>Period</InputLabel>

                        <Select
                            value={granularity}
                            label="Period"
                            onChange={({ target }) =>
                                setGranularity(target.value)
                            }
                        >
                            <MenuItem value="day">Daily</MenuItem>
                            <MenuItem value="week">Weekly</MenuItem>
                            <MenuItem value="month">Monthly</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            </Stack>

            <Box component={HoverPaper}>
                {loading || error || !data.length ? (
                    <Typography
                        variant="body2"
                        color={error ? "error" : "text.secondary"}
                        sx={{ p: 3 }}
                    >
                        {loading
                            ? "Loading chart..."
                            : error || "No spend data for this period"}
                    </Typography>
                ) : (
                    <EChartsReact
                        option={chartOption}
                        notMerge
                        lazyUpdate
                        style={{ height: 450 }}
                    />
                )}
            </Box>
        </Box>
    );
};

const GoogleAdsScreen = () => {
    const auth = useSelector((state) => state.auth);
    const js = useSelector((state) => state.globalReportFilters);

    const { id: hotelID } = useSelector((state) => state.hotelID);
    const validHotel = Number.isInteger(Number(hotelID)) && Number(hotelID) > 0;

    const { data: hotels = [] } = useSelector(selectHotels);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [dailyCostData, setDailyCostData] = useState({
        data: [],
        loading: false,
        error: "",
    });
    const [dailyCostSelection, setDailyCostSelection] = useState({
        hotelID: null,
    });

    const selectedHotelData = useMemo(
        () =>
            data.find(({ HotelID }) => String(HotelID) === String(hotelID)) ??
            null,
        [data, hotelID],
    );

    useEffect(() => {
        if (!js.fromDay || !js.toDay || !auth.user?.jwt) return;

        let active = true;

        setLoading(true);
        setError("");

        Axios.get(`${URLAPI}/google_ads/summary`, {
            params: {
                from: js.fromDay,
                to: js.toDay,
                groupBy: "hotel",
            },
            headers: {
                Authorization: `Bearer ${auth.user.jwt}`,
            },
        })
            .then(({ data: response }) => {
                if (active) setData(response.data ?? []);
            })
            .catch((error) => {
                if (!active) return;

                setData([]);
                setError(
                    error.response?.data?.data?.error ??
                        "Unable to load Google Ads data",
                );
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [auth.user?.jwt, js.fromDay, js.toDay]);

    useEffect(() => {
        if (!js.fromDay || !js.toDay || !auth.user?.jwt) return;

        let active = true;

        setDailyCostData({
            data: [],
            loading: true,
            error: "",
        });

        Axios.get(`${URLAPI}/google_ads/summary`, {
            params: {
                from: js.fromDay,
                to: js.toDay,
                groupBy: "day",
            },
            headers: {
                Authorization: `Bearer ${auth.user.jwt}`,
            },
        })
            .then(({ data: response }) => {
                if (active)
                    setDailyCostData({
                        data: response.data ?? [],
                        loading: false,
                        error: "",
                    });
            })
            .catch((error) => {
                if (!active) return;

                setDailyCostData({
                    data: [],
                    loading: false,
                    error:
                        error.response?.data?.data?.error ??
                        "Unable to load daily Google Ads spend",
                });
            });

        return () => {
            active = false;
        };
    }, [auth.user?.jwt, js.fromDay, js.toDay]);

    const [breakdownData, setBreakdownData] = useState({
        conversionActions: [],
        devices: [],
        loading: false,
        error: "",
    });

    useEffect(() => {
        if (!js.fromDay || !js.toDay || !auth.user?.jwt || !validHotel) {
            setBreakdownData({
                conversionActions: [],
                devices: [],
                loading: false,
                error: "",
            });
            return;
        }

        let active = true;

        setBreakdownData({
            conversionActions: [],
            devices: [],
            loading: true,
            error: "",
        });

        Promise.all(
            ["conversionAction", "device"].map((breakdown) =>
                Axios.get(`${URLAPI}/google_ads/breakdown`, {
                    params: {
                        from: js.fromDay,
                        to: js.toDay,
                        hotelID,
                        breakdown,
                    },
                    headers: {
                        Authorization: `Bearer ${auth.user.jwt}`,
                    },
                }),
            ),
        )
            .then(([conversionActions, devices]) => {
                if (!active) return;

                setBreakdownData({
                    conversionActions: conversionActions.data.data ?? [],
                    devices: devices.data.data ?? [],
                    loading: false,
                    error: "",
                });
            })
            .catch((error) => {
                if (!active) return;

                setBreakdownData({
                    conversionActions: [],
                    devices: [],
                    loading: false,
                    error:
                        error.response?.data?.data?.error ??
                        "Unable to load Google Ads breakdown",
                });
            });

        return () => {
            active = false;
        };
    }, [auth.user?.jwt, hotelID, validHotel, js.fromDay, js.toDay]);

    const [campaignData, setCampaignData] = useState({
        data: [],
        previousData: [],
        loading: false,
        error: "",
    });

    useEffect(() => {
        if (!js.fromDay || !js.toDay || !auth.user?.jwt || !validHotel) {
            setCampaignData({
                data: [],
                previousData: [],
                loading: false,
                error: "",
            });
            return;
        }

        const from = new Date(`${String(js.fromDay).split("T")[0]}T00:00:00Z`);
        const to = new Date(`${String(js.toDay).split("T")[0]}T00:00:00Z`);
        const days = Math.round((to - from) / 86400000) + 1;
        const previousTo = new Date(from);
        const previousFrom = new Date(from);

        previousTo.setUTCDate(previousTo.getUTCDate() - 1);
        previousFrom.setUTCDate(previousFrom.getUTCDate() - days);

        let active = true;

        setCampaignData({
            data: [],
            previousData: [],
            loading: true,
            error: "",
        });

        const { fromDayPrevious, toDayPrevious } = getComparisonDates(js);
        Promise.all(
            [
                [js.fromDay, js.toDay],
                [fromDayPrevious, toDayPrevious],
            ].map(([dateFrom, dateTo]) =>
                Axios.get(`${URLAPI}/google_ads/summary`, {
                    params: {
                        from: dateFrom,
                        to: dateTo,
                        hotelID,
                        groupBy: "campaign",
                    },
                    headers: {
                        Authorization: `Bearer ${auth.user.jwt}`,
                    },
                }),
            ),
        )
            .then(([current, previous]) => {
                if (!active) return;

                setCampaignData({
                    data: current.data.data ?? [],
                    previousData: previous.data.data ?? [],
                    loading: false,
                    error: "",
                });
            })
            .catch((error) => {
                if (!active) return;

                setCampaignData({
                    data: [],
                    previousData: [],
                    loading: false,
                    error:
                        error.response?.data?.data?.error ??
                        "Unable to load campaign performance",
                });
            });

        return () => {
            active = false;
        };
    }, [auth.user?.jwt, hotelID, validHotel, js.fromDay, js.toDay]);

    return (
        <>
            <Container maxWidth={false}>
                <DataLoader Reservations SimpleData />
                <Typography variant="h4" sx={{ mt: 2, mb: 2 }}>
                    Google Ads reports Beta
                </Typography>
                <Typography variant="subtitle1" color="initial">
                    This is a work in progress report. This reports combines
                    data from the Google Ads campaigns and the reservations data
                    from the hotels. It allows to analyze the performance of the
                    campaigns in terms of reservations, revenue, and other KPIs.
                </Typography>
                <br />

                <FiltersReport
                    defaultValues={{
                        presetDate: PRESET_YEAR_TO_DATE,
                    }}
                    hideGeneratePDF
                    hide={[
                        "DATATYPE",
                        "ARRIVAL_PERIOD",
                        "TIME_ARRIVAL_RANGE",
                        "RESDATE_PERIOD",
                        "TIME_BOOKING_RANGE",
                        "FILTERS",
                        "CANCELLATIONS",
                        // "COMPARISON",
                        "TARGET",
                        "CUSTOM_FILTERS",
                        "ACTIVE_FILTERS_LINE",
                    ]}
                    filtersOrder={["TIME_PERIOD", "TIME_RANGE"]}
                    keepAppliedFiltersOnHotelChange
                />

                <GoogleAdsHotelsTable
                    data={data}
                    loading={loading}
                    error={error}
                    onSelectHotel={(hotelID) =>
                        setDailyCostSelection({ hotelID })
                    }
                />

                <GoogleAdsHotelsChart
                    data={data}
                    loading={loading}
                    error={error}
                />

                <GoogleAdsDailyCostChart
                    data={dailyCostData.data}
                    loading={dailyCostData.loading}
                    error={dailyCostData.error}
                    selection={dailyCostSelection}
                    onSelectAll={() => setDailyCostSelection({ hotelID: null })}
                />

                {!loading && selectedHotelData && (
                    <ReportBuilder
                        key={`google-ads-${hotelID}-${js.fromDay}-${js.toDay}`}
                        reports={[
                            {
                                type: REPORT_TABLE_METRICS_SUMMARY,
                                dataType: DATATYPE_RESDATE,
                                groupBy: [],
                                filters: {
                                    Cancellations: "excluded",
                                },
                                fieldsOrder: [
                                    "adsImpressions",
                                    "adsClicks",
                                    "adsCtr",
                                    "adsSpend",
                                    "adsCpc",
                                    "adsConversions",
                                    "adsConversionRate",
                                    "adsCostPerConversion",
                                    "hotelBookings",
                                    "hotelRevenue",
                                    "blendedCostPerBooking",
                                    "blendedRevenueToSpend",
                                ],
                                hideFields: [],
                                customProcess: (args) => {
                                    const processed = process_(args);
                                    const entry = Object.values(
                                        processed.entries ?? {},
                                    )[0] ?? {
                                        keys: [],
                                        result: {},
                                        resultOneYearBefore: {},
                                        data: [],
                                    };

                                    const impressions =
                                        Number(selectedHotelData.Impressions) ||
                                        0;
                                    const clicks =
                                        Number(selectedHotelData.Clicks) || 0;
                                    const conversions =
                                        Number(selectedHotelData.Conversions) ||
                                        0;
                                    const spend =
                                        Number(
                                            selectedHotelData.CostConverted,
                                        ) || 0;
                                    const bookings =
                                        Number(
                                            entry.result?.bookingsNotCancelled,
                                        ) || 0;
                                    const revenue =
                                        Number(entry.result?.revenueRoom) || 0;

                                    return {
                                        ...processed,
                                        entries: {
                                            summary: {
                                                ...entry,
                                                result: {
                                                    adsImpressions: impressions,
                                                    adsClicks: clicks,
                                                    adsCtr: impressions
                                                        ? (clicks /
                                                              impressions) *
                                                          100
                                                        : 0,
                                                    adsConversions: conversions,
                                                    adsSpend: spend,
                                                    adsCpc: clicks
                                                        ? spend / clicks
                                                        : 0,
                                                    adsCostPerConversion:
                                                        conversions
                                                            ? spend /
                                                              conversions
                                                            : 0,
                                                    adsConversionRate: clicks
                                                        ? (conversions /
                                                              clicks) *
                                                          100
                                                        : 0,
                                                    hotelBookings: bookings,
                                                    hotelRevenue: revenue,
                                                    blendedCostPerBooking:
                                                        bookings
                                                            ? spend / bookings
                                                            : 0,
                                                    blendedRevenueToSpend: spend
                                                        ? revenue / spend
                                                        : 0,
                                                },
                                            },
                                        },
                                    };
                                },
                                props: {
                                    title: `Google Ads and reservations — ${
                                        getHotelName(
                                            selectedHotelData.HotelID,
                                            hotels,
                                            false,
                                        ) ??
                                        `Hotel ${selectedHotelData.HotelID}`
                                    }`,
                                    hideExtra: true,
                                    metrics: GOOGLE_ADS_SUMMARY_METRICS,
                                },
                            },
                        ]}
                    />
                )}
                {selectedHotelData && (
                    <GoogleAdsCampaignsTable
                        data={campaignData.data}
                        previousData={campaignData.previousData}
                        loading={campaignData.loading}
                        error={campaignData.error}
                    />
                )}
                <GoogleAdsConversionActions
                    validHotel={validHotel}
                    data={breakdownData.conversionActions}
                    loading={breakdownData.loading}
                    error={breakdownData.error}
                />

                <GoogleAdsDevicePerformance
                    validHotel={validHotel}
                    data={breakdownData.devices}
                    loading={breakdownData.loading}
                    error={breakdownData.error}
                />
                <GoogleAdsForecastSimulator hotelID={hotelID} />
            </Container>
        </>
    );
};

export default GoogleAdsScreen;
